import { listDocAssets } from "@/lib/document/docAssetsClient";
import {
  getDocDocument,
  getDocVersion,
  parseBlocksSnapshot,
} from "@/lib/document/documentsClient";
import type { DocAsset } from "@/lib/document/assetTypes";
import type { DocBlock } from "@/lib/document-builder/blocks/blockTypes";
import { evaluateDocFormulaFields } from "@/lib/document-builder/formulas/docFormulaEval";
import type { DocFormulaEvaluation } from "@/lib/document-builder/formulas/docFormulaEval";
import type { DocVariableValues } from "@/lib/document-builder/variables/variableFill";
import { resolveLogoSrc } from "@/lib/document-builder/pdf/pdfLogo";
import { flattenDocBlocks } from "@/lib/document-builder/blocks/blockFlatten";

export type ProposalEmbeddedDocPage = {
  docDocumentId: string;
  title: string;
  versionNumber: number;
  blocks: DocBlock[];
  assets: DocAsset[];
  variableValues: DocVariableValues;
  formulaEvaluation: DocFormulaEvaluation;
  assetSrcById: Record<string, string>;
};

async function resolveAssetImageSrcs(assets: DocAsset[]): Promise<Record<string, string>> {
  const map: Record<string, string> = {};
  for (const asset of assets) {
    if (!asset.signed_url) continue;
    const src = await resolveLogoSrc(asset.signed_url);
    if (src) map[asset.id] = src;
  }
  return map;
}

function collectImageAssetIds(blocks: readonly DocBlock[]): Set<string> {
  const ids = new Set<string>();
  for (const block of flattenDocBlocks(blocks)) {
    if (block.type === "image" && block.asset_id) ids.add(block.asset_id);
  }
  return ids;
}

/** Load published Document Builder snapshots referenced by an alternate manifest. */
export async function loadProposalEmbeddedDocPages(
  docDocumentIds: readonly string[],
): Promise<{ ok: true; pages: ProposalEmbeddedDocPage[] } | { ok: false; error: string }> {
  const uniqueIds = [...new Set(docDocumentIds.filter(Boolean))];
  if (uniqueIds.length === 0) return { ok: true, pages: [] };

  const assetsRes = await listDocAssets({ signMedia: true });
  const allAssets = assetsRes.ok ? assetsRes.data.assets : [];

  const pages: ProposalEmbeddedDocPage[] = [];

  for (const docDocumentId of uniqueIds) {
      const docRes = await getDocDocument(docDocumentId);
      if (!docRes.ok) return { ok: false, error: docRes.error };
      const publishedId = docRes.data.document.published_version_id;
      if (!publishedId) {
        return {
          ok: false,
          error: `Document "${docRes.data.document.name}" is not published — publish before attaching to a proposal alternate.`,
        };
      }

      const verRes = await getDocVersion({ documentId: docDocumentId, versionId: publishedId });
      if (!verRes.ok) return { ok: false, error: verRes.error };

      const blocks = parseBlocksSnapshot(verRes.data.version.blocks_snapshot);
      const variableValues: DocVariableValues = {};
      const formulaEvaluation = evaluateDocFormulaFields({ blocks, variableValues });
      const neededAssetIds = collectImageAssetIds(blocks);
      const assets = allAssets.filter((a) => neededAssetIds.has(a.id));
      const assetSrcById = await resolveAssetImageSrcs(assets);

      pages.push({
        docDocumentId,
        title: docRes.data.document.name,
        versionNumber: verRes.data.version.version_number,
        blocks,
        assets,
        variableValues,
        formulaEvaluation,
        assetSrcById,
      });
    }
  return { ok: true, pages };
}
